import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import localUtil from '../../lib/util';
import attestConfigs from '../../lib/attestConfigs';
import { resolveAttestJournal } from '../../lib/attestJournal';
import { animalAge } from '../../lib/animalAge';

// Map config keys to actual journal schema fields + Norwegian labels.
// Journal schema reference: backend/lib/class/mongoose/journalSchema.js
//   chiefComplaint, physicalExaminationFindings, diagnosis/diagnoses, treatmentPlan
const JOURNAL_FIELD_MAP = {
    anamnese: {
        label: 'Anamnese',
        get: journal => journal?.chiefComplaint || '',
    },
    findings: {
        label: 'Undersøkelse',
        get: journal => journal?.physicalExaminationFindings || '',
    },
    diagnoses: {
        label: 'Diagnoser',
        get: journal => {
            if (Array.isArray(journal?.diagnoses) && journal.diagnoses.length > 0) {
                const arr = journal.diagnoses
                    .map(d => {
                        const label = `${d.diagnosticCode || d.code || ''} ${d.name || ''}`.trim();
                        if (!label) return '';
                        return d.differential ? `${label} (differensialdiagnose)` : label;
                    })
                    .filter(Boolean)
                    .join(', ');
                if (arr) return arr;
            }
            if (journal?.diagnosis) {
                return journal.diagnosis;
            }
            // Structured DyreID/Pyramidion diagnosis codes (set by the diagnosis picker,
            // e.g. "Avlivet") live in diagnoseCodes, not diagnoses/diagnosis. Fall back to
            // them so the attest fills even when only a code was registered.
            if (Array.isArray(journal?.diagnoseCodes) && journal.diagnoseCodes.length > 0) {
                return journal.diagnoseCodes
                    .map(d => `${d.diagnoseCode || d.code || ''} ${d.name || ''}`.trim())
                    .filter(Boolean)
                    .join(', ');
            }
            return '';
        },
    },
    treatment: {
        label: 'Behandling',
        get: journal => journal?.treatmentPlan || '',
    },
};

function renderBlankLines(count) {
    const lines = [];
    for (let i = 0; i < count; i += 1) {
        lines.push(<div style='border-bottom: 1px solid #999; height: 1.6em;'>&nbsp;</div>);
    }
    return lines;
}

@observer
class AttestLayout extends Component {
    render() {
        const { id, animalId } = this.props;
        const { userStore, saleStore } = this.props.stores;
        const { user, customer } = userStore;
        const { newSale = {}, attestJournals = [] } = saleStore;

        const config = attestConfigs[parseInt(id, 10)];
        if (!config) {
            return <div>Ukjent attest-id: {id}</div>;
        }

        const { visitors = [], animals = [], journals = [] } = newSale;
        const animalIdInt = animalId ? parseInt(animalId, 10) : null;
        const animal = animalIdInt
            ? (animals.find(a => a.id === animalIdInt) || animals[0] || {})
            : (animals[0] || {});
        const owner = visitors[0] || {};

        // The relevant journal can live on the current sale OR on a previous/closed sale
        // (e.g. the euthanasia journal for a deceased animal). Look across both so the
        // attest auto-fills even when printed outside the original visit.
        const journalHasContent = j => (config.includeJournalFields || []).some(key => {
            const mapping = JOURNAL_FIELD_MAP[key];
            return mapping && mapping.get(j);
        });
        const journal = resolveAttestJournal(animal.id, [journals, attestJournals], journalHasContent);

        const vetName = journal.veterinaryName
            || [user?.firstname, user?.lastname].filter(Boolean).join(' ')
            || '';
        const vetHpr = journal.veterinaryHpr || user?.licenseNumber || '';

        const today = util.formatDate(new Date(), { locale: 'nb-NO' });

        return (
            <div class='viewForm'>
                <div class='viewForm receipt'>

                    {/* 1. Clinic header */}
                    <div class='d-flex flex-row justify-content-between align-items-start'>
                        <div>
                            {customer?.images && customer.images.length > 0 && (
                                <img
                                    src={localUtil.displayImg(customer.images[0], this.props, '120x')}
                                    class='img-fluid'
                                    style='max-height: 100px;'
                                />
                            )}
                        </div>
                        <div class='d-flex flex-column text-end' style='font-size: 0.9em;'>
                            <span class='fw-bold'>{customer?.name}</span>
                            <span>{customer?.address}</span>
                            <span>{customer?.postalcode} {customer?.postalplace || customer?.place}</span>
                            <span>Tlf: {customer?.phone}</span>
                            <span>E-post: {customer?.email}</span>
                            {customer?.orgNumber && <span>Orgnr: {customer.orgNumber}</span>}
                        </div>
                    </div>
                    <hr />

                    {/* 2. Attest title */}
                    <div class='d-flex flex-column align-items-center my-3'>
                        <h3 class='m-0 fw-bold text-uppercase'>{config.title}</h3>
                        {config.subtitle && <div class='text-muted'>{config.subtitle}</div>}
                    </div>
                    <div class='d-flex flex-row justify-content-end'>
                        <span class='fw-bold'>Utstedt: {today}</span>
                    </div>

                    {/* 3. Parties block */}
                    <div class='d-flex flex-row mt-3'>
                        <div class='w-50 pe-2'>
                            <div class='fw-bold mb-1'>Eier / Dyreholder</div>
                            <table class='table table-bordered table-sm m-0'>
                                <tbody>
                                    <tr>
                                        <td class='position-relative pt-3 pb-0'>
                                            <small class='position-absolute text-muted' style='top: 0; left: 4px;'>Navn:</small>
                                            {`${owner.firstname || ''} ${owner.lastname || ''}`.trim() || <>&nbsp;</>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class='position-relative pt-3 pb-0'>
                                            <small class='position-absolute text-muted' style='top: 0; left: 4px;'>Adresse:</small>
                                            {owner.address || <>&nbsp;</>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class='position-relative pt-3 pb-0'>
                                            <small class='position-absolute text-muted' style='top: 0; left: 4px;'>Postnr/Sted:</small>
                                            {owner.postalcode || ''} {owner.place || <>&nbsp;</>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class='position-relative pt-3 pb-0'>
                                            <small class='position-absolute text-muted' style='top: 0; left: 4px;'>Telefon:</small>
                                            {owner.cellphone || <>&nbsp;</>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class='position-relative pt-3 pb-0'>
                                            <small class='position-absolute text-muted' style='top: 0; left: 4px;'>E-post:</small>
                                            {owner.email || <>&nbsp;</>}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class='w-50 ps-2'>
                            <div class='fw-bold mb-1'>Dyr</div>
                            <table class='table table-bordered table-sm m-0'>
                                <tbody>
                                    <tr>
                                        <td class='position-relative pt-3 pb-0'>
                                            <small class='position-absolute text-muted' style='top: 0; left: 4px;'>Navn:</small>
                                            {animal.name || <>&nbsp;</>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class='position-relative pt-3 pb-0'>
                                            <small class='position-absolute text-muted' style='top: 0; left: 4px;'>Art / rase:</small>
                                            {animal.species || ''}{animal.breed ? ` / ${animal.breed}` : ''}
                                            {!animal.species && !animal.breed && <>&nbsp;</>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class='position-relative pt-3 pb-0'>
                                            <small class='position-absolute text-muted' style='top: 0; left: 4px;'>Kjønn:</small>
                                            {animal.gender || <>&nbsp;</>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class='position-relative pt-3 pb-0'>
                                            <small class='position-absolute text-muted' style='top: 0; left: 4px;'>Født:</small>
                                            {animal.birth
                                                ? <>{util.formatDate(animal.birth, { locale: 'nb-NO' })} ({animalAge(animal.birth)})</>
                                                : <>&nbsp;</>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class='position-relative pt-3 pb-0'>
                                            <small class='position-absolute text-muted' style='top: 0; left: 4px;'>Farge:</small>
                                            {animal.color || <>&nbsp;</>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class='position-relative pt-3 pb-0'>
                                            <small class='position-absolute text-muted' style='top: 0; left: 4px;'>ChipID:</small>
                                            {animal.chipId || <>&nbsp;</>}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class='position-relative pt-3 pb-0'>
                                            <small class='position-absolute text-muted' style='top: 0; left: 4px;'>Øremerke:</small>
                                            {animal.earMark || <>&nbsp;</>}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* 4. Main attest body */}
                    <div class='mt-3'>
                        <p>{config.mainParagraph}</p>
                    </div>

                    {/* Journal data (only for attester where config.includeJournalFields is non-empty) */}
                    {config.includeJournalFields && config.includeJournalFields.length > 0 && (
                        <div class='mt-2'>
                            <table class='table table-bordered table-sm m-0'>
                                <tbody>
                                    {config.includeJournalFields.map(key => {
                                        const mapping = JOURNAL_FIELD_MAP[key];
                                        if (!mapping) return null;
                                        const value = mapping.get(journal);
                                        return (
                                            <tr>
                                                <td style='width: 25%;' class='fw-bold align-top'>
                                                    {mapping.label}
                                                    <div class='text-muted' style='font-size: 0.75em; font-weight: normal;'>Hentet fra journal</div>
                                                </td>
                                                <td class='align-top' style='white-space: pre-wrap;'>
                                                    {value || <em class='text-muted'>Ikke registrert i journal</em>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* 5. Attest-specific fields (blank lines for handwriting) */}
                    <div class='mt-3'>
                        {config.extraFields.map(field => (
                            <div class='mb-2'>
                                <div class='fw-bold' style='font-size: 0.95em;'>{field.label}:</div>
                                {renderBlankLines(field.blankLines)}
                            </div>
                        ))}
                    </div>

                    {/* 6. Declaration + signature */}
                    <div class='mt-4'>
                        <p><em>Jeg bekrefter at opplysningene i denne attesten er gitt etter beste faglige skjønn.</em></p>

                        <div class='d-flex flex-row mt-3'>
                            <div class='w-50 pe-3'>
                                <div class='fw-bold'>Sted og dato:</div>
                                {renderBlankLines(1)}
                            </div>
                            <div class='w-50 ps-3'>
                                <div class='fw-bold'>Veterinær: {vetName || <>&nbsp;</>}</div>
                                <div>HPR-nr: {vetHpr || <>&nbsp;</>}</div>
                                <div class='mt-2'>Signatur:</div>
                                {renderBlankLines(1)}
                            </div>
                        </div>
                    </div>

                    <hr class='mt-4' />

                    {/* 7. Footer */}
                    <div class='row'>
                        <div class='col-3 d-flex flex-column'>
                            <small>
                                <div class='mb-2 fw-bold'><Text id='dyrejournal.business-register'>The Business Register</Text></div>
                                <div>{customer?.orgNumber}</div>
                                <div>{customer?.name}</div>
                            </small>
                        </div>
                        <div class='col-3 d-flex flex-column'>
                            <small>
                                <div class='mb-2 fw-bold'><Text id='dyrejournal.address'>Address</Text></div>
                                <div>{customer?.address}</div>
                                <div>{customer?.postalcode} {customer?.postalplace || customer?.place}</div>
                            </small>
                        </div>
                        <div class='col-3 d-flex flex-column'>
                            <small>
                                <div class='mb-2 fw-bold'>{customer?.fax ? <Text id='dyrejournal.phone-fax'>Phone/Fax</Text> : <Text id='dyrejournal.phone'>Phone</Text>}</div>
                                <div>{customer?.phone}</div>
                            </small>
                        </div>
                        <div class='col-3 d-flex flex-column'>
                            <small>
                                <div class='mb-2 fw-bold'><Text id='dyrejournal.internet'>Internet</Text></div>
                                <div>{customer?.email}</div>
                                <div>{customer?.webpage}</div>
                            </small>
                        </div>
                    </div>

                </div>
            </div>
        );
    }
}

export default AttestLayout;
